// /home/cockskiz/new.greatlysocial.com/frontend/src/pages/dashboard/post/instagram/InstagramPostService.js
/**
 * Publishes to Instagram via
 *   POST /api/posting/instagram/publish  (Node backend).
 *
 * The backend now supports:
 *   • Feed / Reel / Story  →  { mediaUrl }
 *   • Carousel             →  { files: [{ url }] }
 */
import axios from 'axios';

/**
 * publishInstagramPost
 * ────────────────────────────────────────────────────────────
 * @param {string}   igId     Instagram Business Account ID
 * @param {string}   caption  Text caption
 * @param {string|Array} media  Single URL **or** array of file objects
 * @param {'feed'|'reel'|'story'|'carousel'} format
 * @returns {Promise<object>} Response JSON
 */
export async function publishInstagramPost(
  igId,
  caption,
  media,
  format = 'feed'
) {
  try {
    // Validation
    if (!igId) {
      throw new Error('Instagram account ID is required');
    }
    
    // Auto-detect format based on media
    let detectedFormat = format;
    if (Array.isArray(media) && media.length > 1) {
      detectedFormat = 'carousel';
    } else if (typeof media === 'string' && (media.endsWith('.mp4') || media.endsWith('.mov'))) {
      // Only auto-detect as reel if not explicitly set to story
      if (format !== 'story') {
        detectedFormat = 'reel';
      }
    }
    
    const body = {
      igId,
      message: caption || '',
      format: detectedFormat,
    };
    
    if (Array.isArray(media)) {
      body.files = media.map((f) => ({ url: f.url || f }));
    } else if (media) {
      body.mediaUrl = media;
    }
    
    const res = await axios.post('/api/posting/instagram/publish', body, {
      withCredentials: true,
      timeout: 60000, // 60 seconds for video processing
    });
    
    console.log('[IG publish] success:', res.data);
    return res.data;
    
  } catch (err) {
    console.error('publishInstagramPost error:', err);
    
    // Extract the actual error message from various possible locations
    let errorMessage = 'Failed to post to Instagram';
    
    // Check if it's a timeout error
    if (err.code === 'ECONNABORTED' && err.message.includes('timeout')) {
      errorMessage = 'Instagram post timed out. Video processing may take longer - please try again.';
    } else if (!err.response && err.message) {
      // Custom validation error
      errorMessage = err.message;
    } else if (err.response?.data) {
      // Check multiple possible error locations in order of preference
      if (err.response.data.error && typeof err.response.data.error === 'string') {
        errorMessage = err.response.data.error;
      } else if (err.response.data.error?.message) {
        errorMessage = err.response.data.error.message;
      } else if (err.response.data.message) {
        errorMessage = err.response.data.message;
      } else if (err.response.data.details) {
        errorMessage = err.response.data.details;
      } else if (err.response.data.error?.error_user_msg) {
        // Instagram/Facebook API specific error message
        errorMessage = err.response.data.error.error_user_msg;
      } else if (err.response.data.error?.error_message) {
        errorMessage = err.response.data.error.error_message;
      } else if (err.response.data.error?.code) {
        // Instagram specific error codes
        errorMessage = getInstagramErrorByCode(err.response.data.error.code, err.response.data.error.message);
      } else if (typeof err.response.data === 'string') {
        errorMessage = err.response.data;
      }
    } else if (err.response?.status) {
      // Handle HTTP status codes
      errorMessage = getInstagramErrorByStatus(err.response.status, err.message);
    } else if (err.message) {
      errorMessage = err.message;
    }
    
    // Throw error with the extracted message
    throw new Error(errorMessage);
  }
}

/**
 * Maps Instagram error codes to user-friendly messages
 */
function getInstagramErrorByCode(code, fallbackMessage) {
  const errorMap = {
    2207026: 'Instagram account not properly connected. Please reconnect your account.',
    2207051: 'Instagram Business account required for this action.',
    100: 'Invalid Instagram parameters. Please check your content.',
    190: 'Instagram access token expired. Please reconnect your account.',
    10: 'Instagram permission denied. Please reconnect with proper permissions.',
    368: 'Instagram temporarily blocked this action. Please try again later.',
    24: 'Instagram rate limit reached. Please wait before posting again.',
    4: 'Instagram API request limit reached. Please try again later.',
    17: 'User request limit reached. Please wait a few minutes.',
    32: 'Page request limit reached. Please try again later.',
    613: 'Custom media limit reached. Please reduce the number of images/videos.',
    9004: 'Content not available. The media may have been deleted.',
    9007: 'Media posted before business account conversion. Cannot promote.',
    25: 'Cannot promote an already promoted post.',
    36000: 'Invalid aspect ratio. Check image dimensions.',
    36001: 'Invalid media format. Use JPG, PNG, or MP4.',
    36002: 'Media file too large. Max 8MB for images, 1GB for videos.',
    36003: 'Video too long. Maximum duration varies by format.',
  };
  
  return errorMap[code] || fallbackMessage || `Instagram error (code: ${code})`;
}

/**
 * Maps HTTP status codes to user-friendly messages
 */
function getInstagramErrorByStatus(status, fallbackMessage) {
  const statusMap = {
    400: 'Invalid Instagram request. Please check your content format.',
    401: 'Instagram authentication failed. Please reconnect your account.',
    403: 'Instagram permission denied. Check your account permissions.',
    404: 'Instagram account not found. Please reconnect.',
    429: 'Instagram rate limit exceeded. Please wait before posting again.',
    500: 'Instagram server error. Please try again later.',
    502: 'Instagram server error. Please try again later.',
    503: 'Instagram service unavailable. Please try again later.',
  };
  
  return statusMap[status] || fallbackMessage || `Instagram error (status: ${status})`;
}

/**
 * Helper function to get user-friendly error messages
 * Can be used by UI components to show better error dialogs
 */
export function getInstagramErrorMessage(error) {
  const message = error.message || error.toString();
  const lowerMessage = message.toLowerCase();
  
  // Map technical errors to user-friendly messages with actions
  if (lowerMessage.includes('token') || lowerMessage.includes('expired') || lowerMessage.includes('not properly connected')) {
    return {
      title: 'Connection Issue',
      message: 'Your Instagram connection has expired. Please reconnect your account in Settings.',
      action: 'reconnect'
    };
  }
  
  if (lowerMessage.includes('permission')) {
    return {
      title: 'Permission Required',
      message: 'Missing Instagram permissions. Please reconnect with all required permissions.',
      action: 'reconnect'
    };
  }
  
  if (lowerMessage.includes('business account required')) {
    return {
      title: 'Business Account Required',
      message: 'This feature requires an Instagram Business account. Please convert your account.',
      action: 'upgrade'
    };
  }
  
  if (lowerMessage.includes('rate limit') || lowerMessage.includes('too many')) {
    return {
      title: 'Slow Down',
      message: 'You\'ve reached Instagram\'s posting limit. Please wait a few minutes before trying again.',
      action: 'wait'
    };
  }
  
  if (lowerMessage.includes('temporarily blocked')) {
    return {
      title: 'Temporarily Blocked',
      message: 'Instagram has temporarily blocked this action. Please try again in a few hours.',
      action: 'wait'
    };
  }
  
  if (lowerMessage.includes('file too large') || lowerMessage.includes('too large')) {
    return {
      title: 'File Too Large',
      message: 'Your media file is too large. Maximum 8MB for images, 1GB for videos.',
      action: 'resize'
    };
  }
  
  if (lowerMessage.includes('invalid') && lowerMessage.includes('format')) {
    return {
      title: 'Invalid Format',
      message: 'Instagram doesn\'t support this file format. Use JPG, PNG, or MP4.',
      action: 'convert'
    };
  }
  
  if (lowerMessage.includes('video') && lowerMessage.includes('long')) {
    return {
      title: 'Video Too Long',
      message: 'Your video exceeds Instagram\'s duration limits. Feed: 60s, Reels: 90s, Stories: 60s.',
      action: 'trim'
    };
  }
  
  if (lowerMessage.includes('timeout')) {
    return {
      title: 'Processing Timeout',
      message: 'Video processing is taking longer than expected. Please try again.',
      action: 'retry'
    };
  }
  
  // Default
  return {
    title: 'Posting Failed',
    message: message,
    action: null
  };
}

/**
 * Validates media before posting (optional client-side validation)
 */
export function validateInstagramMedia(media, format) {
  const errors = [];
  
  if (format === 'carousel' && Array.isArray(media)) {
    if (media.length < 2) {
      errors.push('Carousel requires at least 2 images/videos');
    }
    if (media.length > 10) {
      errors.push('Carousel maximum is 10 items');
    }
  }
  
  if (format === 'story' && !media) {
    errors.push('Story requires an image or video');
  }
  
  if (format === 'reel' && media) {
    const isVideo = typeof media === 'string' && 
      (media.endsWith('.mp4') || media.endsWith('.mov'));
    if (!isVideo) {
      errors.push('Reels require a video file');
    }
  }
  
  return errors;
}